// What a top-up's state means to somebody reading a screen, and what colour it is drawn in.
//
// **A purchase is opened before anything is charged**, so `open` covers both "the payment page is
// still up" and "the money left and its event has not arrived". A direct debit is the one case the
// row does tell apart: the invoicing service says when the bank starts collecting it, and the top-up
// then carries `payment_processing_since` until the debit succeeds or fails. For a card the two stay
// indistinguishable, which is why the reconciliation exists and why the history offers it rather
// than guessing.

import type { CreditPurchaseView } from '@/api/billing/purchases'
import type { RecurringChargeEntry } from '@/api/usage/usage'

/** The backend's own four words for a sale. */
export type PurchaseStatus = 'open' | 'completed' | 'expired' | 'cancelled'

/**
 * What a screen calls a top-up's state: the backend's four words, and two of its own.
 *
 * `declined` (« Paiement refusé », `docs/plans/billing-ui.md` 7.5) is a top-up still `open` whose
 * last payment attempt failed — the provider refused it, or the bank did. To the backend it is
 * still open, and it is: it can be paid again until its deadline. But « En attente » over a refused
 * card reads as *wait*, when what it calls for is *pay again, another way*. An attempt the customer
 * cancelled themselves is not a refusal and keeps the plain open state.
 *
 * `confirmation` (« Confirmation demandée », the owner's decision of 2026-10-04) is the same open
 * top-up whose last attempt failed on `authentication_required`: the bank asked for 3-D Secure,
 * which nobody answered — typically an automatic top-up charged with nobody present. **That is not
 * a refusal**: the card is fine, and paying again on session, where the bank's challenge can be
 * answered, is all it takes.
 */
export type DisplayedPurchaseStatus = PurchaseStatus | 'declined' | 'confirmation' | 'collecting'

type FailureFacts = {
  status: string
  payment_failure_reason?: string | null
  payment_processing_since?: string | null
}

/**
 * Whether a payment of this top-up is being collected — a direct debit, for several business days
 * (« Prélèvement en cours »). The backend says so only while the top-up is open and no failure came
 * after; the credits land when the debit arrives, and until then the top-up is neither paid again
 * nor cancelled: either would risk charging the buyer twice or closing a sale whose money is on its
 * way.
 */
export function isCollecting(purchase: Pick<FailureFacts, 'status' | 'payment_processing_since'>): boolean {
  return purchase.status === 'open' && !!purchase.payment_processing_since
}

/** Whether an open top-up is only waiting for its payer to answer their bank's confirmation. */
export function needsConfirmation(purchase: FailureFacts): boolean {
  return purchase.status === 'open' && purchase.payment_failure_reason === 'authentication_required'
}

/** The word to show, under `billing.purchases.status`; a status this build was not taught passes through. */
export function displayedPurchaseStatus(purchase: FailureFacts): string {
  if (isCollecting(purchase)) return 'collecting'
  if (needsConfirmation(purchase)) return 'confirmation'
  if (
    purchase.status === 'open' &&
    purchase.payment_failure_reason &&
    purchase.payment_failure_reason !== 'cancelled_by_customer'
  ) {
    return 'declined'
  }
  return purchase.status
}

/** Vuetify's colour for each. `open` is not a warning: it is the ordinary state of a new top-up. */
export function purchaseColor(status: string): string {
  const colors: Record<string, string> = {
    open: 'info',
    collecting: 'info',
    declined: 'error',
    confirmation: 'warning',
    completed: 'success',
    expired: 'grey',
    cancelled: 'grey',
  }
  return colors[status] ?? 'grey'
}

/**
 * Whether asking the invoicing service what became of this top-up can still change anything.
 *
 * A row that never reached that service (`opened_at_ledger: false`) has nothing to ask about, and
 * one already `completed` has nothing left to settle — the call is harmless either way, but an
 * offer that resolves nothing is worse than no offer.
 */
export function isReconcilable(purchase: CreditPurchaseView): boolean {
  return purchase.opened_at_ledger && purchase.status !== 'completed'
}

/**
 * Whether this top-up can still be paid from here: opened at the invoicing service, still `open`,
 * before its deadline, and with no payment of it being collected.
 *
 * A row that never reached that service has no sale to collect on, and one past `expires_at` is
 * refused by it — an offer that can only answer a refusal is worse than no offer. `now` is a
 * parameter so a screen decides once per render rather than once per row.
 */
export function isPayable(purchase: CreditPurchaseView, now: number = Date.now()): boolean {
  return (
    purchase.opened_at_ledger &&
    purchase.status === 'open' &&
    !isCollecting(purchase) &&
    new Date(purchase.expires_at).getTime() > now
  )
}

/**
 * Whether this top-up can still be cancelled (`billing-ui.md` U8): it is `open`, so nothing has
 * completed it, and no payment of it is being collected. Offered past its deadline and on a row
 * that never reached the invoicing service too — the server closes both, and asking it is how a
 * person learns which.
 */
export function isCancellable(
  purchase: Pick<CreditPurchaseView, 'status'> &
    Partial<Pick<CreditPurchaseView, 'payment_processing_since'>>,
): boolean {
  return purchase.status === 'open' && !isCollecting(purchase)
}

/**
 * Whether the balance is low enough to be worth saying so.
 *
 * **Derived from the server's own runway**, not from a threshold in cents chosen here: the wallet
 * read computes `runway_days` over the same two windows the low-balance email averages, so a
 * screen and that message cannot tell one customer two different things.
 */
export function isRunwayShort(runwayDays: number | null | undefined): boolean {
  return typeof runwayDays === 'number' && runwayDays <= 14
}

/** Vuetify's colour for a monthly fee's dunning state. */
export function chargeColor(status: RecurringChargeEntry['status']): string {
  const colors: Record<string, string> = {
    active: 'success',
    past_due: 'warning',
    suspended: 'error',
    cancelled: 'grey',
  }
  return colors[status] ?? 'grey'
}

/**
 * **Why the last payment attempt failed, as a person reads it.** The backend answers ledger's own
 * vocabulary (`billing_sales_payment_failure_reason_valid`) and a raw word such as
 * `authentication_required` is not something to show anybody; each has a sentence of its own under
 * `billing.purchases.failureReasons`. A word this build was not taught reads as the unknown one
 * rather than as itself.
 */
export const PAYMENT_FAILURE_REASONS = [
  'insufficient_funds',
  'instrument_unusable',
  'authentication_required',
  'refused',
  'cancelled_by_customer',
  'provider_unavailable',
  'unknown',
] as const

export function paymentFailureReasonKey(reason: string): string {
  const known = (PAYMENT_FAILURE_REASONS as readonly string[]).includes(reason) ? reason : 'unknown'
  return `billing.purchases.failureReasons.${known}`
}
